import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate, useParams } from 'react-router-dom'
import {
  addToItems,
  changeQty,
  db,
  findByBarcode,
  kvGet,
  mergeOrders,
  moveOrder,
  openOrderForTable,
  callNumber,
  saveItems,
  servicePercentText,
  setCustomerName,
  setGuests,
  syncServiceFee,
  setOrderNote,
  voidOrder,
  type OrderItem,
  type Product,
  type TableRef,
} from '../lib/db'
import { defaultPrinter, printReceipt, type PrinterConfig } from '../lib/agent'
import { bare } from '../lib/menuImages'
import { useBarcodeScanner } from '../lib/scanner'
import { clock, dateText, elapsedText, money, qtyToText, QTY_UNIT } from '../lib/money'
import { useApp } from '../lib/store'
import { useNow } from '../lib/hooks'
import { DiscountSheet } from '../components/DiscountSheet'
import { CustomerSheet } from '../components/CustomerSheet'
import { GuestSheet } from '../components/GuestSheet'
import { NoteSheet } from '../components/NoteSheet'
import { PageHead } from '../components/PageHead'
import { Sheet } from '../components/Sheet'
import { TablePicker, type PickMode } from '../components/TablePicker'
import { ScooterGlyph } from '../components/ScooterIcon'
import { TableGlyph, TableIcon } from '../components/TableIcon'
import { Thumb } from '../components/Thumb'
import { WeightSheet } from '../components/WeightSheet'

/**
 * Gọi món cho một bàn, bộ Modern.
 *
 * Hai khoang trên cùng một màn hình: thực đơn bên trái, tờ bill của bàn bên phải. Người
 * bán đứng cạnh bàn với cái máy bảng trên tay, khách đọc tới đâu bấm tới đó, và mắt khách
 * cũng nhìn vào chính cái màn hình ấy — nên tờ bill phải luôn hiện, không nằm sau một nút
 * "xem giỏ" như bộ Classic vốn làm cho màn hình điện thoại 5 inch.
 *
 * Bộ Classic giữ nguyên màn hình cũ. Ai đã quen tay thì không bị đổi chỗ bấm.
 */

// ── biểu tượng ──
//
// Vẽ bằng nét thay vì dán emoji: emoji mỗi hệ máy một kiểu, và cái nào cũng mang sẵn màu
// riêng nên đặt cạnh nhau thì hàng nút trông như một nắm kẹo. Nét thì ăn theo màu chữ,
// và đổi theo nền sáng tối cùng với mọi thứ còn lại.

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

const Glyph = ({ d, className }: { d: string; className?: string }) => (
  <svg viewBox="0 0 24 24" className={className ?? 'glyph'} aria-hidden="true" focusable="false">
    <path d={d} {...stroke} />
  </svg>
)

const PATH = {
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-4.2-4.2',
  dots: 'M6 12h.01M12 12h.01M18 12h.01',
  pencil: 'M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17z',
  people: 'M9 11a3.2 3.2 0 1 0 0-6.4A3.2 3.2 0 0 0 9 11zM3 19.5c0-3 2.7-5 6-5s6 2 6 5M16 5.2a3.2 3.2 0 0 1 0 6.2M17.5 14.9c2 .6 3.5 2.2 3.5 4.6',
  clock: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM12 7.5V12l3 1.8',
  move: 'M4 8h11M12 5l3 3-3 3M20 16H9M12 13l-3 3 3 3',
  trash: 'M4 7h16M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7M6.5 7l.9 11.6a1.5 1.5 0 0 0 1.5 1.4h6.2a1.5 1.5 0 0 0 1.5-1.4L17.5 7M10 11v5.5M14 11v5.5',
  printer: 'M7 9V4h10v5M7 18H5.5A1.5 1.5 0 0 1 4 16.5v-5A1.5 1.5 0 0 1 5.5 10h13a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-1.5 1.5H17M7 14h10v6H7z',
  save: 'M5 4h11l3 3v13H5zM8 4v5h7V4M8 20v-6h8v6',
  card: 'M3 8.5A1.5 1.5 0 0 1 4.5 7h15A1.5 1.5 0 0 1 21 8.5v7a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 15.5zM3 11h18M6.5 14.5h3',
  plus: 'M12 6v12M6 12h12',
  close: 'M6.5 6.5l11 11M17.5 6.5l-11 11',
  note: 'M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17zM14 6.5l3.5 3.5',
  grid: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
} as const

/**
 * Biểu tượng của một nhóm món, đoán từ chính cái tên nhóm.
 *
 * Danh mục trong app chỉ có tên, không có ô biểu tượng — mà bắt chủ quán chọn hình cho
 * từng nhóm trước khi bán được ly đầu tiên là thêm một cái rào. Đoán trúng thì hàng nút
 * dễ nhắm hơn; đoán trượt thì rơi về cái đĩa, cũng không sai gì.
 */
const CAT_GLYPHS: [RegExp, string][] = [
  [/tra sua|tran chau/, '🧋'],
  [/ca phe|coffee|cafe/, '☕'],
  [/\btra\b|matcha/, '🍵'],
  [/sinh to/, '🥤'],
  [/nuoc ep|ep trai cay/, '🧃'],
  [/bia|ruou|coctail|cocktail/, '🍺'],
  [/kem|yaourt|sua chua/, '🍨'],
  [/banh|trang mieng|dessert|ngot/, '🍰'],
  [/pho|bun|mi|hu tieu|chao/, '🍜'],
  [/com|dia/, '🍚'],
  [/lau|nuong/, '🍲'],
  [/an vat|snack|khai vi|an kem/, '🍟'],
  [/do uong|thuc uong|nuoc/, '🥤'],
  [/do an|mon an|thuc an/, '🍽️'],
  // "Khác" là cái nhóm ai cũng có mà chẳng nói lên điều gì — để nó ba chấm, đúng như bản mẫu.
  [/^khac$|^linh tinh$/, '⋯'],
]

function catGlyph(name: string): string {
  const plain = bare(name)
  for (const [re, glyph] of CAT_GLYPHS) if (re.test(plain)) return glyph
  return '🍽️'
}

/**
 * Xem "Tất cả" thì mỗi nhóm chỉ bày bốn món.
 *
 * Màn hình này là chỗ nhìn lướt: bốn món đầu của mỗi nhóm đủ để người bán thấy quán có
 * những gì, muốn xem hết một nhóm thì bấm chip của nhóm ấy ngay phía trên. Bày cả 40 món
 * thì cuộn mãi không tới cái mình cần.
 *
 * Con số 4 đi cặp với số cột trong `skin-modern.css`: lưới chỉ có 4 cột hoặc 2 cột, nên
 * bốn món luôn lấp kín hàng, không bao giờ để lại một ô lẻ loi.
 */
const PER_GROUP = 4

/** Hai hàng bốn cột. Quá số này là phải cuộn, mà cuộn thì mất chỗ đang đứng trong đầu. */
const PAGE_SIZE = 8

/** Mã giả cho những món chủ quán chưa xếp vào nhóm nào. */
const NO_CAT = '__none'

type Tab = 'items' | 'note' | 'history'

export function OrderModernScreen() {
  const { orderId = '' } = useParams()
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const shop = useApp((s) => s.shop)
  const now = useNow(30_000)

  const [catId, setCatId] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [pageAt, setPageAt] = useState(1)
  const [tab, setTab] = useState<Tab>('items')
  const [menu, setMenu] = useState(false)
  const [picking, setPicking] = useState<PickMode | null>(null)
  const [noting, setNoting] = useState<OrderItem | null>(null)
  const [weighing, setWeighing] = useState<Product | null>(null)
  const [discounting, setDiscounting] = useState(false)
  const [guesting, setGuesting] = useState(false)
  const [naming, setNaming] = useState(false)
  const [printing, setPrinting] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')

  const order = useLiveQuery(() => db.orders.get(orderId), [orderId])
  const categories = useLiveQuery(() => db.categories.orderBy('sortOrder').toArray(), [], [])
  const products = useLiveQuery(() => db.products.orderBy('sortOrder').toArray(), [], [])
  const zones = useLiveQuery(() => db.zones.toArray(), [], [])

  const tableId = order?.tableId ?? null
  const table = useLiveQuery(
    async () => (tableId ? await db.diningTables.get(tableId) : undefined),
    [tableId],
  )

  /*
   * Cùng một màn hình phục vụ hai lối bán, nên nó phải tự xưng đúng tên.
   *
   * Đơn của quầy mang về không có cái bàn nào để chuyển đi hay gộp vào, và người bán vào
   * đây từ mục "Mang về" chứ không từ sơ đồ bàn — gọi nó là "Bán tại bàn" rồi thả họ về
   * sơ đồ bàn khi bấm quay lại là đưa nhầm đường.
   */
  const takeaway = table?.isTakeaway ?? false
  /** Xong việc ở đây thì trả người bán về đúng chỗ họ vừa đi ra. */
  const backTo = takeaway ? '/takeaway' : '/tables'
  /** Gọi cái đang mở là gì. Khách mang về không ngồi bàn nào, gọi là "đơn". */
  const spot = takeaway ? 'đơn' : 'bàn'

  /**
   * Những lượt khách trước của chính cái bàn này, trong hôm nay.
   *
   * Câu hỏi có thật ở quán đông: "bàn này vừa nãy ai ngồi, đã tính tiền chưa" — khách
   * quay lại đòi thêm ly nước đã trả tiền, hoặc hai ca giao nhau giữa buổi. Đọc thẳng từ
   * máy nên mất mạng vẫn tra được.
   */
  const past = useLiveQuery(
    async () => {
      if (!tableId) return []
      const start = new Date()
      start.setHours(0, 0, 0, 0)
      const from = start.toISOString()

      const rows = await db.orders.where('tableId').equals(tableId).toArray()
      return rows
        .filter((o) => o.id !== orderId && o.status === 'Paid' && (o.paidAt ?? '') >= from)
        .sort((a, b) => (b.paidAt ?? '').localeCompare(a.paidAt ?? ''))
    },
    [tableId, orderId],
    [],
  )

  // Quán vừa đổi mức phí phục vụ thì bàn đang ngồi cũng phải tính theo mức mới.
  useEffect(() => {
    void syncServiceFee(orderId)
  }, [orderId])

  // Ghi chú gõ vào ô trống rồi mới ghi xuống máy lúc rời ô. Ghi từng phím là mỗi chữ cái
  // một lần ghi đĩa và một lần đánh dấu "chưa đồng bộ" — vẽ lại cả khoang bên phải.
  useEffect(() => {
    setNoteDraft(order?.note ?? '')
  }, [order?.note])

  const items = order?.items ?? []
  const fee = order?.serviceFee ?? 0
  const guests = order?.guests ?? 0

  const q = bare(query)

  // Đổi nhóm hoặc gõ chữ tìm là về trang đầu — đứng ở trang 3 của nhóm cũ rồi nhảy sang
  // nhóm mới chỉ có 1 trang thì màn hình trống trơn.
  useEffect(() => {
    setPageAt(1)
  }, [catId, q])

  /** Có món chưa xếp nhóm thì cho nó một nhóm, không thì nó biến mất khỏi thực đơn. */
  const hasLoose = useMemo(() => products.some((p) => !p.categoryId), [products])

  /**
   * Danh sách món đang bày, đã phẳng thành một lưới.
   *
   * Ba đường vào cùng đổ về một danh sách: đang tìm thì là kết quả tìm, đang chọn nhóm
   * thì là cả nhóm, còn "Tất cả" thì là bốn món đầu của từng nhóm nối lại — đủ để liếc
   * qua xem quán có những gì, muốn xem hết một nhóm thì bấm chip của nhóm ấy.
   */
  const listed = useMemo(() => {
    const inCat = (id: string) => (p: Product) =>
      id === NO_CAT ? !p.categoryId : p.categoryId === id

    // So sau khi bỏ dấu: người bán gõ "tra dao" phải ra "Trà đào", vì lúc đang đứng
    // trước mặt khách thì không ai gõ đủ dấu.
    if (q) return products.filter((p) => bare(p.name).includes(q) || (p.barcode ?? '').includes(q))
    if (catId) return products.filter(inCat(catId))

    const groups = categories.map((c) => c.id)
    if (hasLoose) groups.push(NO_CAT)
    return groups.flatMap((id) => products.filter(inCat(id)).slice(0, PER_GROUP))
  }, [products, categories, catId, q, hasLoose])

  /*
   * Phân trang thay cho cuộn.
   *
   * Người bán đứng gõ đơn bằng một ngón tay giữa lúc khách đợi. Cuộn thì lần nào cũng
   * phải tìm lại chỗ, còn số trang thì đứng yên: "cà phê muối ở trang 1, trà vải trang 2"
   * là thứ nhớ được sau một buổi, và nhớ rồi thì bấm không cần nhìn.
   *
   * Hiện <b>hết</b> số trang, không rút gọn bằng dấu ba chấm. Rút gọn thì vị trí nút số 5
   * đổi chỗ theo trang đang đứng, mà đổi chỗ là mất luôn cái trí nhớ ngón tay ấy.
   */
  const pageCount = Math.max(1, Math.ceil(listed.length / PAGE_SIZE))
  const page = Math.min(pageAt, pageCount)
  const shown = listed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const qtyOf = (productId: string) =>
    items.filter((i) => i.productId === productId).reduce((s, i) => s + i.qtyMilli, 0)

  /**
   * Riêng một biến thể đã gọi bao nhiêu.
   *
   * Con số ở góc thẻ là tổng cả món, nên "Trà vải" hiện số 1 mà người bán vẫn không biết
   * ly ấy là M hay L. Bàn gọi thêm ly nữa mà bấm nhầm cỡ thì tới lúc pha mới lòi ra.
   */
  const qtyOfOption = (productId: string, option: string) =>
    items
      .filter((i) => i.productId === productId && i.optionLabel === option)
      .reduce((s, i) => s + i.qtyMilli, 0)

  // Dòng trong bill chỉ giữ tên và giá lúc bán, không giữ mã ảnh — tra ngược ra sản phẩm.
  const imageOf = (productId: string | null) =>
    productId ? (products.find((x) => x.id === productId)?.imageKey ?? null) : null

  async function add(p: Product, option: string | null) {
    if (!order) return
    if (p.soldByWeight) {
      setWeighing(p)
      return
    }
    await saveItems(order.id, addToItems(items, p, option))
  }

  useBarcodeScanner({
    enabled: !weighing && !menu && !picking && !noting && !discounting && !guesting,
    onScan: async (code) => {
      const found = await findByBarcode(code)
      if (!found) {
        say(`Chưa gán mã ${code} cho món nào`)
        return
      }
      if (found.categoryId) setCatId(found.categoryId)
      await add(found, null)
      say(`+ ${found.name}`)
    },
  })

  async function bump(item: OrderItem, delta: number) {
    if (!order) return
    await saveItems(order.id, changeQty(items, item.id, delta * QTY_UNIT))
  }

  async function drop(itemId: string) {
    if (!order) return
    await saveItems(
      order.id,
      items.filter((i) => i.id !== itemId),
    )
  }

  async function cancelOrder() {
    if (!order) return
    await voidOrder(order.id)
    say(`Đã huỷ ${order.tableName ?? order.code}`)
    nav(backTo)
  }

  /** Khách đổi chỗ giữa chừng — dời cả đơn sang bàn trống, giờ ngồi giữ nguyên. */
  async function doMove(to: TableRef) {
    if (!order) return
    const from = order.tableName
    await moveOrder(order.id, to)
    setPicking(null)
    say(`Đã chuyển ${from ?? 'đơn'} sang ${to.name}`)
  }

  /** Hai nhóm ghép bàn — dồn món sang bàn kia rồi đóng đơn bên này. */
  async function doMerge(to: TableRef, targetOrderId: string | undefined) {
    if (!order) return
    const intoId = targetOrderId ?? (await openOrderForTable(to)).id
    await mergeOrders(order.id, intoId)
    setPicking(null)
    say(`Đã gộp vào ${to.name}`)
    nav(`/order/${intoId}`)
  }

  /**
   * In tờ tạm tính đưa khách xem trước khi thu tiền.
   *
   * Không cắt giấy, không mở két, không mang số hoá đơn — khách còn ngồi, chưa trả tiền.
   * Máy chưa chạy chương trình in thì nói thẳng ra câu ấy, chứ không giấu cái nút đi:
   * nút biến mất thì người bán tưởng app không in được, và đi mua phần mềm khác.
   */
  async function printDraft() {
    if (!order || printing) return
    setPrinting(true)
    try {
      const cfg = await kvGet<PrinterConfig>('printer')
      await printReceipt(order, shop, cfg?.address ? cfg : defaultPrinter, { provisional: true })
      say('Đã in tạm tính')
    } catch (e) {
      say(e instanceof Error ? e.message : 'Không in được')
    }
    setPrinting(false)
  }

  async function saveNote() {
    if (!order) return
    if ((noteDraft.trim() || null) === (order.note ?? null)) return
    await setOrderNote(order.id, noteDraft)
  }

  if (!order) return <div className="screen" />

  const zoneName = zones.find((z) => z.id === table?.zoneId)?.name ?? null
  const noteCount = items.filter((i) => i.note).length

  /*
   * Số dòng và số phần là hai con số khác nhau, và người bưng bê cần con số thứ hai.
   *
   * Khách gọi bốn ly cà phê muối thì bốn ly ấy nằm gọn trên <b>một dòng</b>. Thẻ chỉ đếm
   * dòng là nói "4 món" trong khi bếp phải pha bảy ly — người bưng ra ba ly rồi quay lại
   * hỏi bàn còn thiếu gì.
   *
   * Chỉ hiện thêm khi hai con số khác nhau. Bàn nào mỗi món một phần thì "(4 · 4 phần)"
   * là hai lần cùng một thông tin, mà hai số giống nhau đặt cạnh nhau là mời đọc nhầm.
   */
  const pieces = items.reduce((sum, i) => sum + i.qtyMilli, 0)
  const piecesLabel = pieces > items.length * QTY_UNIT ? ` · ${qtyToText(pieces)} phần` : ''

  return (
    <div className="screen">
      <div className="board dine-board">
        <div className="dine-split">
          <div className="dine-main">
            <PageHead
              to={backTo}
              icon={takeaway ? <ScooterGlyph /> : <TableGlyph />}
              tone={takeaway ? 'is-clay' : undefined}
              title={takeaway ? 'Mang về' : 'Bán tại bàn'}
              sub={order.code}
            />

            <div className="cat-bar">
              <div className="cat-row" role="tablist" aria-label="Nhóm món">
              <button
                type="button"
                role="tab"
                className="cat-tile"
                aria-selected={catId === null && !q}
                onClick={() => {
                  setCatId(null)
                  setQuery('')
                  setSearching(false)
                }}
              >
                <span className="cat-glyph" aria-hidden="true">
                  <Glyph d={PATH.grid} className="glyph" />
                </span>
                <span className="cat-name">Tất cả</span>
              </button>

              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="tab"
                  className="cat-tile"
                  aria-selected={catId === c.id && !q}
                  onClick={() => {
                    setCatId(c.id)
                    setQuery('')
                    setSearching(false)
                  }}
                >
                  <span className="cat-glyph" aria-hidden="true">
                    {catGlyph(c.name)}
                  </span>
                  <span className="cat-name">{c.name}</span>
                </button>
              ))}

              {hasLoose && (
                <button
                  type="button"
                  role="tab"
                  className="cat-tile"
                  aria-selected={catId === NO_CAT && !q}
                  onClick={() => {
                    setCatId(NO_CAT)
                    setQuery('')
                    setSearching(false)
                  }}
                >
                  <span className="cat-glyph" aria-hidden="true">
                    ⋯
                  </span>
                  <span className="cat-name">Khác</span>
                </button>
              )}
              </div>

              {/*
                Tìm món thu về một cái kính lúp.
                Ô gõ chỉ bung ra khi cần, và bung ra <b>ngay tại chỗ</b> chứ không nổi đè
                lên lưới món: người bán gõ ở trên, món hiện ngay bên dưới, không có gì
                che mất thứ họ đang tìm.
              */}
              <button
                type="button"
                className="cat-search"
                aria-expanded={searching}
                aria-label="Tìm món"
                title="Tìm món ăn, thức uống"
                onClick={() => {
                  if (searching) setQuery('')
                  setSearching((v) => !v)
                }}
              >
                <Glyph d={searching ? PATH.close : PATH.search} />
              </button>
            </div>

            {searching && (
              <div className="menu-search">
                <Glyph d={PATH.search} className="glyph-sm" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Tìm món ăn, thức uống…"
                  inputMode="search"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') return
                    setQuery('')
                    setSearching(false)
                  }}
                />
                {query && (
                  <button type="button" onClick={() => setQuery('')} aria-label="Xoá chữ đang tìm">
                    <Glyph d={PATH.close} className="glyph-sm" />
                  </button>
                )}
              </div>
            )}

            {listed.length === 0 ? (
              <div className="empty">
                <div className="empty-icon" aria-hidden="true">
                  🔍
                </div>
                <p>
                  {q
                    ? `Không có món nào khớp "${query.trim()}".`
                    : 'Quán chưa có món nào. Vào Sản phẩm để thêm.'}
                </p>
              </div>
            ) : (
              <>
                <div className="menu-grid">
                  {shown.map((p) => {
                      const inBill = qtyOf(p.id)
                      return (
                        <div key={p.id} className={`menu-card${inBill > 0 ? ' is-on' : ''}`}>
                          {inBill > 0 && (
                            <span className="menu-count num">{qtyToText(inBill)}</span>
                          )}

                          <button
                            type="button"
                            className="menu-face"
                            onClick={() => void add(p, null)}
                            aria-label={`Thêm ${p.name}, ${money(p.price)} đồng`}
                          >
                            <Thumb name={p.name} imageKey={p.imageKey} className="menu-thumb" />
                            <span className="menu-body">
                              <b className="menu-name">{p.name}</b>
                              <span className="menu-price num">{money(p.price)}đ</span>
                            </span>
                            <span className="menu-add" aria-hidden="true">
                              <Glyph d={PATH.plus} className="glyph-sm" />
                            </span>
                          </button>

                          {/* Hàng tuỳ chọn luôn có mặt, món không có biến thể thì để trống.
                              Thiếu nó thì thẻ ấy thấp hơn hàng bên cạnh, và lưới trông
                              lởm chởm — mà lưới lởm chởm thì hàng số trang cũng nhảy. */}
                          {p.quickOptions.length === 0 ? (
                            <div className="product-opts is-blank" aria-hidden="true" />
                          ) : (
                            <div className="product-opts">
                              {p.quickOptions.map((o) => {
                                const q = qtyOfOption(p.id, o)
                                return (
                                  <button
                                    key={o}
                                    type="button"
                                    aria-pressed={q > 0}
                                    onClick={() => void add(p, o)}
                                  >
                                    {o}
                                    {q > 0 && <b className="num">{qtyToText(q)}</b>}
                                  </button>
                                )
                              })}
                            </div>
                          )}
                      </div>
                    )
                  })}

                  {/*
                    Ô trống cho đủ một trang.
                    Trang cuối chỉ có hai món thì lưới vẫn cao bằng trang đầy, nên hàng số
                    trang đứng nguyên một chỗ — đó là thứ làm nên trí nhớ ngón tay. Ô trống
                    dựng bằng đúng bộ khung của thẻ món nên cao đúng bằng, không phải đoán.
                  */}
                  {pageCount > 1 &&
                    Array.from({ length: PAGE_SIZE - shown.length }, (_, i) => (
                      <div key={`pad-${i}`} className="menu-card is-pad" aria-hidden="true">
                        <span className="menu-thumb" />
                        <span className="menu-body">
                          <b className="menu-name">&nbsp;</b>
                          <span className="menu-price">&nbsp;</span>
                        </span>
                        <div className="product-opts is-blank" />
                      </div>
                    ))}
                </div>

                {pageCount > 1 && (
                  <nav className="menu-pages" aria-label="Trang món">
                    {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
                      <button
                        key={n}
                        type="button"
                        className="menu-page num"
                        aria-current={n === page ? 'page' : undefined}
                        onClick={() => setPageAt(n)}
                      >
                        {n}
                      </button>
                    ))}
                  </nav>
                )}
              </>
            )}
          </div>

          <aside className="bill-pane" aria-label={`Đơn của ${order.tableName ?? 'khách'}`}>
            <div className="bill-table">
              {/*
                Số khách nằm luôn trên ô hình cái bàn.
                Trước đây nó là một dòng chữ "Số khách: —" chiếm nguyên nửa hàng, mà thứ
                đáng chiếm chỗ trong khoang hẹp này là danh sách món. Bấm vào ô hình là
                sửa, giống hệt cái bút chì bên cạnh.
              */}
              <button
                type="button"
                className="bill-table-icon"
                onClick={() => setGuesting(true)}
                aria-label={guests > 0 ? `${guests} khách. Bấm để sửa` : 'Chưa đếm số khách'}
                title="Số khách"
              >
                <TableIcon icon={table?.icon} />
                <span className="bill-guests num">{guests > 0 ? guests : '–'}</span>
              </button>

              <div className="bill-table-name">
                <h2>{order.tableName ?? 'Bán nhanh'}</h2>
                <button
                  type="button"
                  className="bill-edit"
                  onClick={() => setGuesting(true)}
                  aria-label="Sửa số khách"
                  title="Sửa số khách"
                >
                  <Glyph d={PATH.pencil} className="glyph-sm" />
                </button>
              </div>

              {/* Khách mang về thì không có bàn nào để đổi chỗ. */}
              {!takeaway && (
                <button
                  type="button"
                  className="bill-move"
                  disabled={!order.tableId}
                  onClick={() => setPicking('move')}
                >
                  <Glyph d={PATH.move} className="glyph-sm" />
                  Chuyển bàn
                </button>
              )}

              <button
                type="button"
                className="round-btn is-plain"
                onClick={() => setMenu(true)}
                aria-label="Thêm lựa chọn"
                title="Thêm lựa chọn"
              >
                <Glyph d={PATH.dots} />
              </button>

              {/* Gộp hai hàng thông tin thành một: bỏ được một hàng là danh sách món dài
                  thêm một dòng, mà khoang này lúc nào cũng thiếu chỗ. */}
              <div className="bill-facts">
                {/*
                  Tên khách và số gọi.
                  Quầy mang về đông người đứng đợi thì đây là thứ nhân viên đọc to khi đồ
                  làm xong. Chưa có tên thì vẫn còn ba số cuối mã đơn, luôn có sẵn.
                */}
                <button type="button" className="bill-who" onClick={() => setNaming(true)}>
                  <span className="bill-call num">{callNumber(order.code)}</span>
                  <b>{order.customerName ?? 'Đặt tên khách'}</b>
                </button>

                <span>
                  Khu vực: <b>{zoneName ?? 'Chưa xếp khu'}</b>
                </span>
                <span>
                  <Glyph d={PATH.clock} className="glyph-sm" />
                  <b className="num">{clock(order.openedAt)}</b>
                  <small className="num"> · {dateText(order.openedAt)}</small>
                </span>
                <span className="bill-elapsed">
                  <Glyph d={PATH.clock} className="glyph-sm" />
                  <b>{elapsedText(order.openedAt, now)}</b>
                </span>
              </div>
            </div>

            <div className="bill-tabs" role="tablist" aria-label={`Nội dung ${spot}`}>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'items'}
                onClick={() => setTab('items')}
              >
                {`Món đang gọi (${items.length}${piecesLabel})`}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'note'}
                onClick={() => setTab('note')}
              >
                Ghi chú{noteCount > 0 ? ` (${noteCount})` : ''}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'history'}
                onClick={() => setTab('history')}
              >
                Lịch sử ({past.length})
              </button>
            </div>

            {tab === 'items' &&
              (items.length === 0 ? (
                <div className="pane-empty">
                  <div className="empty-icon" aria-hidden="true">
                    🧾
                  </div>
                  <p>Bấm vào món bên trái, hoặc quét mã vạch để thêm vào {spot} này.</p>
                </div>
              ) : (
                <div className="bill-lines">
                  <div className="bill-line bill-line-head">
                    <span>#</span>
                    <span>Món ăn / Thức uống</span>
                    <span>SL</span>
                    <span>Đơn giá</span>
                    <span>Thành tiền</span>
                    <span />
                  </div>

                  {items.map((i, n) => (
                    <div key={i.id} className="bill-line">
                      <span className="bill-n num">{n + 1}</span>

                      <button
                        type="button"
                        className="bill-item line-note"
                        onClick={() => setNoting(i)}
                      >
                        <Thumb
                          name={i.name}
                          imageKey={imageOf(i.productId)}
                          className="bill-thumb"
                        />
                        <span className="bill-item-text">
                          <b>
                            {i.name}
                            {i.optionLabel && <em> · {i.optionLabel}</em>}
                          </b>
                          {i.note ? (
                            <small className="line-note-text">{i.note}</small>
                          ) : (
                            <small className="line-note-add">+ ghi chú</small>
                          )}
                        </span>
                      </button>

                      <div className="stepper">
                        <button
                          type="button"
                          onClick={() => void bump(i, -1)}
                          aria-label={`Bớt ${i.name}`}
                        >
                          −
                        </button>
                        <span className="num">{qtyToText(i.qtyMilli)}</span>
                        <button
                          type="button"
                          onClick={() => void bump(i, 1)}
                          aria-label={`Thêm ${i.name}`}
                        >
                          +
                        </button>
                      </div>

                      <span className="bill-unit num">{money(i.unitPrice)}đ</span>
                      <b className="bill-amount num">{money(i.lineTotal)}đ</b>

                      <button
                        type="button"
                        className="bill-drop"
                        onClick={() => void drop(i.id)}
                        aria-label={`Bỏ ${i.name} khỏi bàn`}
                        title="Bỏ món"
                      >
                        <Glyph d={PATH.trash} className="glyph-sm" />
                      </button>
                    </div>
                  ))}
                </div>
              ))}

            {tab === 'note' && (
              <div className="bill-notes">
                <label className="field">
                  <span>Ghi chú cho {spot} này</span>
                  <textarea
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onBlur={() => void saveNote()}
                    placeholder="Khách quen · ngồi ngoài hiên · chờ bạn tới rồi mới lên món…"
                    maxLength={250}
                    rows={3}
                  />
                  <small className="note">In lên tờ bill và lên phiếu xuống bếp.</small>
                </label>

                {noteCount > 0 && (
                  <div className="bill-note-list">
                    <h3>Ghi chú theo món</h3>
                    {items
                      .filter((i) => i.note)
                      .map((i) => (
                        <button
                          key={i.id}
                          type="button"
                          className="bill-note-row"
                          onClick={() => setNoting(i)}
                        >
                          <b>{i.name}</b>
                          <small>{i.note}</small>
                          <Glyph d={PATH.pencil} className="glyph-sm" />
                        </button>
                      ))}
                  </div>
                )}
              </div>
            )}

            {tab === 'history' && (
              <div className="bill-history">
                {past.length === 0 ? (
                  <div className="pane-empty">
                    <div className="empty-icon" aria-hidden="true">
                      🕐
                    </div>
                    <p>
                      {takeaway
                        ? 'Hôm nay quầy mang về chưa có đơn nào tính tiền xong.'
                        : 'Hôm nay bàn này chưa có lượt khách nào tính tiền xong.'}
                    </p>
                  </div>
                ) : (
                  past.map((o) => (
                    <div key={o.id} className="bill-past">
                      <div className="bill-past-main">
                        <b>{o.code}</b>
                        <small>
                          {o.paidAt ? clock(o.paidAt) : '—'} ·{' '}
                          {qtyToText(o.items.reduce((s, i) => s + i.qtyMilli, 0))} món
                          {o.guests ? ` · ${o.guests} khách` : ''}
                        </small>
                      </div>
                      <b className="num">{money(o.total)}đ</b>
                    </div>
                  ))
                )}
              </div>
            )}

            {tab === 'items' && (
              <button type="button" className="bill-note-add" onClick={() => setTab('note')}>
                <Glyph d={PATH.note} className="glyph-sm" />
                {order.note ? (
                  <span className="bill-note-has">{order.note}</span>
                ) : (
                  <span>Thêm ghi chú cho {spot} này…</span>
                )}
              </button>
            )}

            <div className="bill-sums">
              <div className="pay-row">
                <span>Tạm tính</span>
                <b className="num">{money(order.subtotal)}đ</b>
              </div>

              <div className="pay-row">
                <span className="bill-disc-label">
                  {order.discount > 0 ? (order.discountLabel ?? 'Giảm giá') : 'Giảm giá'}
                  <button
                    type="button"
                    className="bill-disc-btn"
                    onClick={() => setDiscounting(true)}
                    disabled={items.length === 0}
                  >
                    <Glyph d={PATH.plus} className="glyph-sm" />
                    {order.discount > 0 ? 'Sửa' : 'Thêm giảm giá'}
                  </button>
                </span>
                <b className="num">{order.discount > 0 ? `−${money(order.discount)}đ` : '0đ'}</b>
              </div>

              {fee > 0 && (
                <div className="pay-row">
                  <span>Phí phục vụ ({servicePercentText(order.serviceFeePermille)}%)</span>
                  <b className="num">{money(fee)}đ</b>
                </div>
              )}
            </div>

            <div className="bill-total">
              <span>Tổng cộng</span>
              <b className="num">{money(order.total)}đ</b>
            </div>

            <div className="bill-actions">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={items.length === 0 || printing}
                onClick={() => void printDraft()}
              >
                <Glyph d={PATH.printer} className="glyph-sm" />
                {printing ? 'Đang in…' : 'In tạm tính'}
              </button>

              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  void saveNote()
                  say(`Đã lưu ${order.tableName ?? order.code}`)
                  nav(backTo)
                }}
              >
                <Glyph d={PATH.save} className="glyph-sm" />
                Lưu tạm
              </button>

              <button
                type="button"
                className="btn btn-brand"
                disabled={items.length === 0}
                onClick={() => nav(`/pay/${order.id}`)}
              >
                <Glyph d={PATH.card} className="glyph-sm" />
                Thanh toán
              </button>
            </div>
          </aside>
        </div>
      </div>

      {menu && (
        <Sheet title={order.tableName ?? 'Bán nhanh'} onClose={() => setMenu(false)}>
          {/* Chuyển bàn và gộp bàn đều là việc của cái bàn, đơn mang về không có bàn. */}
          {!takeaway && (
            <button
              type="button"
              className="row-btn"
              disabled={!order.tableId}
              onClick={() => {
                setMenu(false)
                setPicking('move')
              }}
            >
              <span className="row-icon" aria-hidden="true">
                ↔️
              </span>
              <span className="row-text">
                <b>Chuyển bàn</b>
                <small>Khách đổi chỗ, dời cả đơn sang bàn trống</small>
              </span>
              <span aria-hidden="true">›</span>
            </button>
          )}

          {!takeaway && (
            <button
              type="button"
              className="row-btn"
              disabled={items.length === 0}
              onClick={() => {
                setMenu(false)
                setPicking('merge')
              }}
            >
              <span className="row-icon" aria-hidden="true">
                ⤵️
              </span>
              <span className="row-text">
                <b>Gộp bàn</b>
                <small>Dồn món của bàn này sang bàn khác</small>
              </span>
              <span aria-hidden="true">›</span>
            </button>
          )}

          <button
            type="button"
            className="row-btn"
            onClick={() => {
              setMenu(false)
              setNaming(true)
            }}
          >
            <span className="row-icon" aria-hidden="true">
              🙋
            </span>
            <span className="row-text">
              <b>Tên khách</b>
              <small>
                {order.customerName ?? `Chưa đặt tên — gọi số ${callNumber(order.code)}`}
              </small>
            </span>
            <span aria-hidden="true">›</span>
          </button>

          <button
            type="button"
            className="row-btn"
            onClick={() => {
              setMenu(false)
              setGuesting(true)
            }}
          >
            <span className="row-icon" aria-hidden="true">
              👥
            </span>
            <span className="row-text">
              <b>Số khách</b>
              <small>{guests > 0 ? `Đang ghi ${guests} khách` : 'Chưa đếm'}</small>
            </span>
            <span aria-hidden="true">›</span>
          </button>

          {/*
            Đơn chưa có món nào cũng phải huỷ được.
            Trước đây nút này chỉ hiện khi đã gọi món, nên một cái bàn lỡ mở ra rồi bỏ đó
            không có đường nào trả về trống — nó nằm lì trên sơ đồ tới hết ngày.
          */}
          <button
            type="button"
            className="btn btn-danger-ghost wide"
            onClick={() => {
              setMenu(false)
              void cancelOrder()
            }}
          >
            {items.length > 0 ? 'Huỷ đơn này' : takeaway ? 'Bỏ đơn này' : 'Trả bàn về trống'}
          </button>
        </Sheet>
      )}

      {picking && (
        <TablePicker
          mode={picking}
          fromTableId={order.tableId}
          onClose={() => setPicking(null)}
          onPick={(t, targetOrder) =>
            picking === 'move' ? doMove(t) : doMerge(t, targetOrder?.id)
          }
        />
      )}

      {guesting && (
        <GuestSheet
          guests={guests}
          tableName={order.tableName ?? 'Bán nhanh'}
          onClose={() => setGuesting(false)}
          onSave={(n) => setGuests(order.id, n)}
        />
      )}

      {naming && (
        <CustomerSheet
          name={order.customerName}
          callNo={callNumber(order.code)}
          onClose={() => setNaming(false)}
          onSave={(n) => setCustomerName(order.id, n)}
        />
      )}

      {discounting && <DiscountSheet order={order} onClose={() => setDiscounting(false)} />}

      {noting && (
        <NoteSheet
          item={noting}
          onClose={() => setNoting(null)}
          onSave={async (note) => {
            await saveItems(
              order.id,
              items.map((x) => (x.id === noting.id ? { ...x, note } : x)),
            )
          }}
        />
      )}

      {weighing && (
        <WeightSheet
          product={weighing}
          onClose={() => setWeighing(null)}
          onConfirm={async (qtyMilli) => {
            const next: OrderItem[] = [
              ...items,
              {
                id: crypto.randomUUID(),
                productId: weighing.id,
                name: weighing.name,
                unitPrice: weighing.price,
                qtyMilli,
                lineTotal: Math.round((weighing.price * qtyMilli) / QTY_UNIT),
                optionLabel: null,
                note: null,
              },
            ]
            await saveItems(order.id, next)
            setWeighing(null)
          }}
        />
      )}
    </div>
  )
}
